'use client';

import type { ComponentProps, ReactNode } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { IconButton } from '../../components/icon-button';
import { BodyDefault } from '../../components/typography';
import { Tooltip } from '../../components/tooltip';
import { DomainScopePathScopeBadge } from './DomainScopePathScopeBadge';

export const TruncatedCellText = ({
  text,
  color,
  fontWeight,
  pl,
}: {
  text: string;
  color?: string;
  fontWeight?: string;
  pl?: string;
}) => (
  <Tooltip title={text}>
    <Box minW={0} truncate pl={pl}>
      <BodyDefault truncate color={color} fontWeight={fontWeight}>
        {text}
      </BodyDefault>
    </Box>
  </Tooltip>
);
export const buildPathScopeBadgeCell = ({
  variant,
  text,
}: {
  variant: ComponentProps<typeof DomainScopePathScopeBadge>['variant'];
  text: string;
}) => (
  <Tooltip title={text}>
    <Box flexShrink={0} whiteSpace="nowrap">
      <DomainScopePathScopeBadge variant={variant}>
        {text}
      </DomainScopePathScopeBadge>
    </Box>
  </Tooltip>
);
export const buildExpandCell = ({
  canExpand,
  isExpanded,
  onToggle,
}: {
  canExpand: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) =>
  canExpand ? (
    <IconButton
      variant="ghost"
      size="xs"
      aria-label={isExpanded ? 'Collapse' : 'Expand'}
      onClick={onToggle}
    >
      {isExpanded ? (
        <ArrowDown01Icon size={14} />
      ) : (
        <ArrowRight01Icon size={14} />
      )}
    </IconButton>
  ) : null;
export const buildDomainPathCell = ({
  domain,
  text,
  renderDomainSuffix,
}: {
  domain: string;
  text: string;
  renderDomainSuffix?: (domain: string) => ReactNode;
}) => (
  <HStack gap={1} alignItems="center" minW={0} w="full">
    <Tooltip title={text}>
      <Box flex="1" minW={0} truncate>
        <BodyDefault truncate>{text}</BodyDefault>
      </Box>
    </Tooltip>
    {renderDomainSuffix?.(domain)}
  </HStack>
);
export const buildActionsCell = (children: ReactNode) => (
  <HStack gap={0} justifyContent="flex-end" w="full" flexShrink={0}>
    {children}
  </HStack>
);

export const DomainScopeTableActionButton = ({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <Tooltip title={label}>
    <IconButton
      variant="ghost"
      size="xs"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </IconButton>
  </Tooltip>
);
